import { PageHero } from '@/components/layout/PageHero';
import { ArrowIcon, ButtonLink } from '@/components/ui/Button';
import { Seo } from '@/features/site/Seo';
import { useSite } from '@/features/site/SiteProvider';
import { CapabilitiesSection, ContactDetails, InquirySection, MapEmbed, ProcessSection, ProductionInfoStrip } from '@/features/site/sections';
import type { LegalDoc } from '@/types/api';

export function PrivateLabelPage() {
  const { process, about } = useSite().content;
  return (
    <>
      <Seo title="Private Label, OEM & ODM Manufacturing" path="/private-label" description={process.intro} />
      <PageHero
        crumbs={[{ label: 'Private Label' }]}
        eyebrow="Private label · OEM · ODM"
        title={
          <>
            Your brand, manufactured <span className="editorial-italic text-accent">from idea to shipment.</span>
          </>
        }
        intro="Bring a tech pack, a reference sample or just a logo. We handle development, sampling, bulk production, quality control, export packing and documentation — you approve every stage."
        photo="embroidery"
        photoPosition="50% 45%"
        actions={
          <>
            <ButtonLink to="#inquiry" variant="accent" size="lg" icon={<ArrowIcon />}>
              Discuss Your Project
            </ButtonLink>
            <ButtonLink to="/apparel" variant="outline" size="lg">
              Browse products
            </ButtonLink>
          </>
        }
      >
        <dl className="mt-12 grid gap-px overflow-hidden rounded-[8px] border border-line bg-line sm:grid-cols-2">
          <div className="bg-white p-5">
            <dt className="eyebrow text-accent-dark">OEM</dt>
            <dd className="mt-2 text-[15px] leading-relaxed text-ink-soft">We manufacture your existing design and specification.</dd>
          </div>
          <div className="bg-white p-5">
            <dt className="eyebrow text-accent-dark">ODM</dt>
            <dd className="mt-2 text-[15px] leading-relaxed text-ink-soft">We develop the product with you from references and add your branding.</dd>
          </div>
        </dl>
        {about.tags.length > 0 && (
          <ul className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-[14px] text-ink-soft" aria-label="Branding and export services">
            {about.tags.map((t) => (
              <li key={t} className="flex items-center gap-2">
                <span aria-hidden className="h-1 w-1 rounded-full bg-accent" />
                {t}
              </li>
            ))}
          </ul>
        )}
      </PageHero>
      <ProcessSection showCtas />
      <CapabilitiesSection />
      <ProductionInfoStrip />
      <InquirySection
        eyebrow="Start a private-label project"
        heading="Tell us about your product"
        intro="Share your requirements and design references. We will review feasibility, specifications and MOQ and reply with a quotation."
        context={{ customization: 'Private label / OEM project' }}
      />
    </>
  );
}

export function ContactPage() {
  const { contactPage } = useSite().content;
  return (
    <>
      <Seo title="Contact Us" path="/contact" description={contactPage.intro} />
      <PageHero crumbs={[{ label: 'Contact' }]} eyebrow={contactPage.eyebrow} title={contactPage.heading} intro={contactPage.intro}>
        <div className="mt-12 grid gap-10 lg:grid-cols-2 lg:gap-12">
          <ContactDetails />
          <MapEmbed />
        </div>
      </PageHero>
      <InquirySection eyebrow="Inquiry form" heading="Send us your requirements" intro="All fields marked * are required. We never share your details." showDetails={false} />
    </>
  );
}

function LegalPage({ title, path, doc }: { title: string; path: string; doc: LegalDoc }) {
  return (
    <>
      <Seo title={title} path={path} />
      <PageHero crumbs={[{ label: title }]} eyebrow="Legal" title={title} intro={doc.lastUpdated ? `Last updated: ${doc.lastUpdated}` : undefined} />
      <article className="container-page max-w-[820px] py-14 lg:py-20">
        <div className="space-y-10">
          {doc.sections.map((s, i) => (
            <section key={s.heading} className="grid gap-3 border-t border-line pt-8 sm:grid-cols-[64px_1fr]">
              <span className="font-display text-[14px] font-bold text-accent-dark tabular-nums">{String(i + 1).padStart(2, '0')}</span>
              <div>
                <h2 className="font-display text-[21px] font-bold tracking-tight text-primary">{s.heading}</h2>
                <p className="mt-3 text-[16px] leading-[1.8] whitespace-pre-line text-ink-soft">{s.body}</p>
              </div>
            </section>
          ))}
        </div>
      </article>
    </>
  );
}

export function PrivacyPolicyPage() {
  return <LegalPage title="Privacy Policy" path="/privacy-policy" doc={useSite().content.privacyPolicy} />;
}

export function TermsPage() {
  return <LegalPage title="Terms & Conditions" path="/terms" doc={useSite().content.terms} />;
}
